'use client'

import { useEffect } from 'react'
import { ShoppingCartIcon, ShoppingBagIcon, CreditCardIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { PopoverClose } from '@radix-ui/react-popover'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { useTranslatedRoute, useAppLocale } from '@/hooks'
import { Link } from '@/i18n/navigation'
import { useTranslations } from 'next-intl'
import { useCartStore, useLocalCartStore, useMeStore } from '@/store'
import { getLocalizedValue, getResizedPictureUrl } from '@/lib/utils'
import LazyImage from '@/components/partials/lazy-image'
import NoImagePlaceholder from '@/components/partials/no-image-placeholder'
import PriceDisplay from '@/components/partials/price-display'

type PropsType = {
  hasToken: boolean
}

const TopCartButton = ({ hasToken }: PropsType) => {
  const t = useTranslations()
  const { buildRoute } = useTranslatedRoute()
  const { backendLocale } = useAppLocale()
  const { cartState, getCart } = useCartStore()
  const localItems = useLocalCartStore(state => state.items)
  const { meState } = useMeStore()

  const isAuthenticated = hasToken && !!meState.response

  const registeredItems = cartState.response?.items ?? []
  const registeredQuantity = cartState.response?.totalQuantity ?? 0
  const guestQuantity = localItems.reduce((acc, i) => acc + i.quantity, 0)
  const totalQuantity = isAuthenticated ? registeredQuantity : guestQuantity

  const cartRoute = buildRoute('cart')
  const checkoutRoute = `${cartRoute}?checkout=1`
  const displayCount = totalQuantity > 9 ? '9+' : totalQuantity > 0 ? String(totalQuantity) : null
  const registeredTotal = cartState.response?.total
  const isCartLoading = isAuthenticated && (cartState.isLoading || !cartState.response)

  const getRegisteredProductHref = (item: (typeof registeredItems)[number]) => {
    const productSlug = getLocalizedValue(item.product, 'slug', backendLocale)
    if (!productSlug) {
      return buildRoute('products')
    }
    const categorySlugs: string[] = []
    const category = item.product.category
    if (category?.parents?.length) {
      category.parents.forEach(parent => {
        const slug = getLocalizedValue(parent, 'slug', backendLocale)
        if (slug) {
          categorySlugs.push(slug)
        }
      })
    }
    if (category) {
      const categorySlug = getLocalizedValue(category, 'slug', backendLocale)
      if (categorySlug) {
        categorySlugs.push(categorySlug)
      }
    }
    return buildRoute('product', ...categorySlugs, productSlug)
  }

  useEffect(() => {
    if (isAuthenticated && !cartState.response && !cartState.isLoading) {
      getCart().catch(() => {})
    }
  }, [isAuthenticated, cartState.response, cartState.isLoading, getCart])

  return (
    <Popover modal={false}>
      <Tooltip>
        <PopoverTrigger asChild>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              className="hover:bg-brand-darken relative h-7! w-7! shrink-0 flex-col items-center p-0 hover:text-white focus-visible:outline-none md:h-9! md:w-9! lg:h-auto! lg:w-auto! lg:gap-0.5 lg:px-2 lg:py-1">
              <div className="relative">
                <ShoppingCartIcon className="size-4 md:size-5" />
                {displayCount && (
                  <span className="absolute -top-2 -right-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-gray-200 px-0.5 text-[10px] font-bold text-gray-700">
                    {displayCount}
                  </span>
                )}
              </div>
              <span className="hidden text-[9px] leading-none font-medium uppercase opacity-80 lg:block">
                {t('cart')}
              </span>
            </Button>
          </TooltipTrigger>
        </PopoverTrigger>
        <TooltipContent>
          <p>{t('shopping_cart')}</p>
        </TooltipContent>
      </Tooltip>

      <PopoverContent align="end" className="z-60! w-72 p-3 select-none sm:w-80">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">{t('shopping_cart')}</span>
            {!isCartLoading && totalQuantity > 0 && (
              <span className="text-muted-foreground text-xs">
                {t('cart_summary', {
                  items: isAuthenticated ? (cartState.response?.totalItems ?? 0) : localItems.length,
                  quantity: totalQuantity
                })}
              </span>
            )}
          </div>

          <Separator />

          {isCartLoading ? (
            <div className="space-y-2 py-1">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center gap-2">
                  <Skeleton className="h-10 w-13.5 rounded" />
                  <div className="flex-1 space-y-1">
                    <Skeleton className="h-2.5 w-16" />
                    <Skeleton className="h-3 w-28" />
                  </div>
                  <div className="space-y-1 text-right">
                    <Skeleton className="h-2.5 w-7" />
                    <Skeleton className="h-3 w-12" />
                  </div>
                </div>
              ))}
            </div>
          ) : totalQuantity === 0 ? (
            <p className="text-muted-foreground py-4 text-center text-sm">{t('cart_is_empty')}</p>
          ) : (
            <ScrollArea type="scroll" viewportClassName="max-h-54">
              <div className="space-y-2">
                {isAuthenticated
                  ? registeredItems.map(item => {
                      const title = getLocalizedValue(item.product, 'title', backendLocale) || item.product.code
                      const href = getRegisteredProductHref(item)
                      return (
                        <PopoverClose asChild key={item.id}>
                          <Link
                            href={href}
                            className="hover:bg-muted/40 flex items-center gap-2 rounded p-1 transition">
                            <div className="relative aspect-4/3 w-13.5 shrink-0 overflow-hidden rounded border bg-gray-50">
                              {item.product.mainPicture ? (
                                <LazyImage
                                  src={getResizedPictureUrl(item.product.mainPicture.path, 80, 60)}
                                  alt={title}
                                  fill
                                  sizes="54px"
                                  className="object-cover"
                                />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center">
                                  <NoImagePlaceholder iconSize={3} />
                                </div>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-muted-foreground truncate text-[10px]">{item.product.code}</p>
                              <p className="truncate text-xs font-medium">{title}</p>
                            </div>
                            <div className="shrink-0 text-right">
                              <span className="block text-xs text-gray-500">×{item.quantity}</span>
                              {item.itemTotal && (
                                <PriceDisplay
                                  gross={item.itemTotal.gross}
                                  originalGross={item.itemTotal.originalGross}
                                  discountRate={item.itemTotal.discountRate}
                                  currencyCode={item.itemTotal.currencyCode}
                                  locale={backendLocale}
                                  size="sm"
                                  className="items-end"
                                  discountedClassName="text-xs font-semibold"
                                  originalClassName="text-[10px]"
                                  badgeClassName="text-[9px] px-1 py-0"
                                />
                              )}
                            </div>
                          </Link>
                        </PopoverClose>
                      )
                    })
                  : localItems.map(item => (
                      <PopoverClose asChild key={item.productId}>
                        <Link
                          href={
                            item.productSlug
                              ? buildRoute('product', ...(item.categorySlugs ?? []), item.productSlug)
                              : buildRoute('products')
                          }
                          className="hover:bg-muted/40 flex items-center gap-2 rounded p-1 transition">
                          <div className="relative aspect-4/3 w-13.5 shrink-0 overflow-hidden rounded border bg-gray-50">
                            {item.mainPicturePath ? (
                              <LazyImage
                                src={getResizedPictureUrl(item.mainPicturePath, 80, 60)}
                                alt={item.productTitle}
                                fill
                                sizes="54px"
                                className="object-cover"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center">
                                <NoImagePlaceholder iconSize={3} />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-muted-foreground truncate text-[10px]">{item.productCode}</p>
                            <p className="truncate text-xs font-medium">{item.productTitle}</p>
                          </div>
                          <span className="shrink-0 text-xs text-gray-500">×{item.quantity}</span>
                        </Link>
                      </PopoverClose>
                    ))}
              </div>
            </ScrollArea>
          )}

          {isAuthenticated && registeredTotal && totalQuantity > 0 && (
            <>
              <Separator />
              <div className="space-y-1.5">
                {registeredTotal.originalGross !== null &&
                  registeredTotal.originalGross !== undefined &&
                  registeredTotal.originalGross !== registeredTotal.gross && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-green-700">{t('discount')}</span>
                      <span className="font-medium text-green-700">
                        -
                        {new Intl.NumberFormat(backendLocale, {
                          style: 'currency',
                          currency: registeredTotal.currencyCode,
                          minimumFractionDigits: 2
                        }).format(registeredTotal.originalGross - registeredTotal.gross)}
                      </span>
                    </div>
                  )}
                {registeredTotal.taxAmount > 0 && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{t('tax')}</span>
                    <span className="text-gray-600">
                      {new Intl.NumberFormat(backendLocale, {
                        style: 'currency',
                        currency: registeredTotal.currencyCode,
                        minimumFractionDigits: 2
                      }).format(registeredTotal.taxAmount)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-700">{t('total')}</span>
                  <PriceDisplay
                    gross={registeredTotal.gross}
                    originalGross={registeredTotal.originalGross}
                    discountRate={registeredTotal.discountRate}
                    currencyCode={registeredTotal.currencyCode}
                    locale={backendLocale}
                    size="sm"
                    discountedClassName="text-sm font-semibold"
                    originalClassName="text-[10px]"
                    badgeClassName="text-[9px] px-1 py-0"
                  />
                </div>
              </div>
            </>
          )}

          <Separator />

          {!isCartLoading && totalQuantity > 0 && (
            <PopoverClose asChild>
              <Button asChild variant="outline" size="sm" className="w-full uppercase">
                <Link href={checkoutRoute}>
                  <CreditCardIcon className="h-4 w-4" />
                  {t(isAuthenticated ? 'checkout' : 'checkout_as_guest')}
                </Link>
              </Button>
            </PopoverClose>
          )}

          <PopoverClose asChild>
            <Button asChild variant="brand" size="sm" className="w-full uppercase">
              <Link href={cartRoute}>
                <ShoppingBagIcon className="h-4 w-4" />
                {t('go_to_cart')}
              </Link>
            </Button>
          </PopoverClose>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default TopCartButton
